import { Checkbox, Flex, Text } from "@chakra-ui/react";
import {
  convertTextAlignToFlex,
  getComponentStyle,
} from "components/containers/InspectionFormat/Content/SettingFontSize/utils/style";
import CheckboxIcon from "components/icon/CheckboxIcon";
import ConnectDeviceIcon from "components/icon/ConnectDeviceIcon";
import DateTimeIcon from "components/icon/DateTimeIcon";
import FloatNumberIcon from "components/icon/FloatNumberIcon";
import ImageIcon from "components/icon/ImageIcon";
import NumbericIcon from "components/icon/NumericIcon";
import PhotoCameraBlackboardIcon from "components/icon/PhotoBlackboardIcon";
import TimeIcon from "components/icon/TimeIcon";
import TyphographyIcon from "components/icon/TyphographyIcon";
import {
  DateNodeAttrs,
  ImageBlackboardNodeAttrs,
  ImageNodeAttrs,
  TaskItemNodeAttrs,
  TaskListNodeAttrs,
} from "components/TipTapEditorDoc/extendsions";
import { TimeNodeAttrs } from "components/TipTapEditorDoc/extendsions/TimeNode";
import { CustomNode, NodeType } from "components/TipTapEditorDoc/type";
import { getContentNodeTypeNormalText } from "components/TipTapEditorDoc/utils";
import { CheckboxDirectionOptionsEnum } from "constants/enum";
import {
  GetBlackboardContent,
  GetContentPreview,
} from "interfaces/models/inspectionDocument";
import {
  AdditionInfo,
  CheckboxAdditionalInfo,
  DateAdditionalInfo,
  DateFormatType,
  DAYJS_FORMAT_STRING,
  DEFAULT_DATE_FROM_PHOTO_ID,
  InspectionItemResult,
  PreFilledAdditionInfo,
  TimeAdditionalInfo,
  TimeFormatType,
  TIME_PICKER_FORMAT_STRING,
} from "interfaces/models/inspectionItemResult";
import { PinDetail } from "interfaces/models/pin";
import { omit } from "lodash-es";
import { transformCheckboxExtraKey } from "models/pinFrame";
import { ReactElement } from "react";
import { formatDate, formatTimeByString } from "utils/date";
import { z, ZodType } from "zod";
import {
  DEFAULT_CHECKBOX_ADDITIONAL_INFO,
  isStringNotEmpty,
} from "../context/FormBuilderContext/types";
import {
  CheckboxEditor,
  CheckboxPreviewer,
  CheckboxRaw,
  DateEditor,
  DatePreviewer,
  DateRaw,
  DevicePressureEditor,
  DevicePressurePreviewer,
  DevicePressureRaw,
  ImageEditor,
  ImageHasBlackboardEditor,
  ImageHasBlackboardPreviewer,
  ImagePreviewer,
  ImageRaw,
  InputEditor,
  InputNumberEditor,
  InputNumberPreviewer,
  InputNumberRaw,
  InputPreviewer,
  InputRaw,
} from "../fields";
import ImageDroppable from "../fields/image/droppable";
import { ImageValue } from "../fields/image/previewer";
import { ImageHasBlackboardRaw } from "../fields/imageHasBlackboard";
import ImageHasBlackboardDroppable from "../fields/imageHasBlackboard/droppable";
import BlackboardContentText from "../fields/imageHasBlackboard/previewer/BlackboardContentText";
import {
  PreFilledEditor,
  PreFilledPreviewer,
  PreFilledRaw,
} from "../fields/pre-filled";
import { TimeEditor, TimePreviewer, TimeRaw } from "../fields/time";
import { changeDataOffline, changeDate } from "../helpers/afterChangeValue";
import withReviewer from "../hooks/withReviewer";
import Image from "components/Image";

export enum DataType {
  IMAGE = 0,
  TEXT = 1,
  NUMBER_INT = 2,
  NUMBER_FLOAT = 3,
  CHECKBOX = 4,
  DEVICE_PRESSURE = 5,
  IMAGE_HAS_BLACKBOARD = 6,
  PRE_FILLED = 7,
  DATE = 8,
  TIME = 9,
}

export const DataTypeNumber = [DataType.NUMBER_INT, DataType.NUMBER_FLOAT];

export const DATE_UPDATE_TRIGGER_DATA_TYPES = [
  DataType.IMAGE,
  DataType.IMAGE_HAS_BLACKBOARD,
];

export const DATA_OFFLINE_UPDATE_TRIGGER_DATA_TYPES = [
  DataType.IMAGE,
  DataType.IMAGE_HAS_BLACKBOARD,
];

export const hooks = {
  afterChangeValue: [changeDate, changeDataOffline],
};

type Types = {
  [key in DataType]: {
    name: string;
    editor: React.FC<any>;
    previewer: React.FC<any>;
    droppable?: React.FC<any>;
    raw: React.FC<any>;
    icon?: ReactElement;
    order: number;
    dataType: DataType;
    getContentPreview: GetContentPreview;
    getBlackboardContent?: GetBlackboardContent;
    getInitData(
      item: Partial<InspectionItemResult>
    ): Partial<InspectionItemResult>;
    clearInitData(
      item: Partial<InspectionItemResult>
    ): Partial<InspectionItemResult>;
    validate?(): any;
  };
};

export const types: Types = {
  [DataType.IMAGE]: {
    name: "写真",
    editor: ImageEditor,
    raw: ImageRaw,
    previewer: withReviewer(ImagePreviewer),
    droppable: withReviewer(ImageDroppable),
    icon: <ImageIcon w="2rem" h="2rem" />,
    order: 0,
    dataType: DataType.IMAGE,
    getInitData: () => {
      return {};
    },
    clearInitData: () => {
      return {};
    },
    getContentPreview: (_, params) => {
      const { pinContent } = params;
      const updatedAt = pinContent?.updatedAt;
      const content = {
        type: NodeType.IMAGE,
        attrs: { src: pinContent?.value?.data?.src },
      } satisfies CustomNode<ImageNodeAttrs>;

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData } = params;
      const data = formData as ImageValue;
      const src = data?.src || "";
      if (!src) {
        return <></>;
      }

      return (
        <Image src={src} maxH="100%" objectFit="contain" w="100%" h="auto" />
      );
    },
  },
  [DataType.IMAGE_HAS_BLACKBOARD]: {
    name: "写真（​黒板あり）",
    editor: ImageHasBlackboardEditor,
    raw: ImageHasBlackboardRaw,
    previewer: withReviewer(ImageHasBlackboardPreviewer),
    droppable: withReviewer(ImageHasBlackboardDroppable),
    icon: <PhotoCameraBlackboardIcon width="2.5rem" height="2.5rem" />,
    order: 1,
    dataType: DataType.IMAGE_HAS_BLACKBOARD,
    getInitData: () => {
      return {};
    },
    clearInitData: () => {
      return {};
    },
    getContentPreview: (_, params) => {
      const { pinContent, pinDetail } = params;
      const updatedAt = pinContent?.updatedAt;
      const omitKeys = [
        "pinGroups",
        "position",
        "pinsSamePosition",
        "viewId",
        "order",
        "createdAt",
        "updatedAt",
        "inspectionTypeId",
        "pinGroupId",
      ] as (keyof PinDetail)[];

      const content = {
        type: NodeType.IMAGE_BLACKBOARD,
        attrs: {
          pinContent,
          pinDetail: omit(pinDetail || {}, omitKeys) as any,
        },
      } satisfies CustomNode<ImageBlackboardNodeAttrs>;

      return { updatedAt, content };
    },
  },
  [DataType.TEXT]: {
    name: "テキスト",
    editor: InputEditor,
    raw: InputRaw,
    previewer: withReviewer(InputPreviewer),
    order: 2,
    dataType: DataType.TEXT,
    getInitData: () => ({}),
    clearInitData: () => ({}),
    icon: <TyphographyIcon w="2.5rem" h="2.5rem" />,
    getContentPreview: (_, params) => {
      const { pinContent, component } = params;
      const updatedAt = pinContent?.updatedAt;
      const content = getContentNodeTypeNormalText({
        text: pinContent?.value?.data || "",
        style: component?.style,
      });

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData, style, zoomRatio } = params;

      return (
        <BlackboardContentText
          value={formData || ""}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
  },
  [DataType.NUMBER_INT]: {
    name: "数値 (整数)",
    editor: InputNumberEditor,
    raw: InputNumberRaw,
    icon: <NumbericIcon w="2.5rem" h="2.5rem" />,
    order: 4,
    previewer: withReviewer(InputNumberPreviewer),
    dataType: DataType.NUMBER_INT,
    getInitData: () => ({}),
    clearInitData: () => ({}),
    getContentPreview: (_, params) => {
      const { pinContent, component } = params;
      const updatedAt = pinContent?.updatedAt;

      const content = getContentNodeTypeNormalText({
        text: pinContent?.value?.data || "",
        style: component?.style,
        options: {
          isInt: true,
        },
      });

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData, style, zoomRatio } = params;

      return (
        <BlackboardContentText
          value={formData || ""}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
  },
  [DataType.NUMBER_FLOAT]: {
    name: "数値 (実数)",
    editor: InputNumberEditor,
    raw: InputNumberRaw,
    icon: <FloatNumberIcon w="2.5rem" h="2.5rem" />,
    order: 5,
    dataType: DataType.NUMBER_FLOAT,
    previewer: withReviewer(InputNumberPreviewer),
    getInitData: () => ({}),
    clearInitData: () => ({}),
    getContentPreview: (_, params) => {
      const { pinContent, component } = params;
      const updatedAt = pinContent?.updatedAt;
      const content = getContentNodeTypeNormalText({
        text: pinContent?.value?.data || "",
        style: component?.style,
        options: {
          isFloat: true,
        },
      });

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData, style, zoomRatio } = params;

      return (
        <BlackboardContentText
          value={formData || ""}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
  },
  [DataType.CHECKBOX]: {
    name: "判定",
    editor: CheckboxEditor,
    raw: CheckboxRaw,
    previewer: withReviewer(CheckboxPreviewer),
    icon: <CheckboxIcon w="2.5rem" h="2.5rem" />,
    order: 6,
    dataType: DataType.CHECKBOX,
    getInitData: (item) => {
      const additionInfo: AdditionInfo = {
        ...item.additionInfo,
        checkbox: DEFAULT_CHECKBOX_ADDITIONAL_INFO,
      };

      return {
        additionInfo: structuredClone(additionInfo),
      } satisfies Partial<InspectionItemResult>;
    },
    clearInitData: () => {
      return {
        additionInfo: {},
      } satisfies Partial<InspectionItemResult>;
    },
    validate: () => {
      return z
        .object({
          checkbox: z
            .object({
              mapValues: z.record(
                z.string(),
                z.object({
                  label: z.string(),
                  displayValue: isStringNotEmpty,
                })
              ),
            })
            .optional() satisfies ZodType<CheckboxAdditionalInfo | undefined>,
        })
        .nullish();
    },
    getContentPreview: (_, params) => {
      const { pinContent, component, itemResult, pinDetail } = params;
      const linkedData = component?.linkedData;
      const mapValues = itemResult?.additionInfo?.checkbox?.mapValues;
      const { extraId, isCheckboxValue, isCheckboxLabel } =
        transformCheckboxExtraKey(linkedData?.extraId);

      const displayValue = mapValues?.[`${extraId}`]?.displayValue;
      const updatedAt = pinContent?.updatedAt;

      if (isCheckboxLabel) {
        const content = getContentNodeTypeNormalText({
          text: mapValues?.[extraId]?.displayValue || "",
          style: component?.style,
          disableEdit: true,
        });

        return { content, updatedAt };
      }

      const content = {
        type: NodeType.TASK_LIST,
        attrs: {
          direction: CheckboxDirectionOptionsEnum.HORIZONTAL,
          type: "item",
          alignItems: component?.style?.alignItems,
        },
        content: [
          {
            type: NodeType.TASK_ITEM,
            attrs: {
              checked:
                pinContent?.value?.data && pinContent?.value?.data == extraId,
              style: component?.style,
              groupId: `${pinDetail?.id}/${linkedData?.key}`,
              key: extraId,
            },
            content:
              displayValue && !isCheckboxValue
                ? ([
                    {
                      type: NodeType.TEXT,
                      text: displayValue,
                    },
                  ] as any)
                : [],
          },
        ] satisfies CustomNode<TaskItemNodeAttrs>[],
      } satisfies CustomNode<TaskListNodeAttrs>;

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData, linkedData, itemResult, style, zoomRatio } = params;
      const mapValues = itemResult?.additionInfo?.checkbox?.mapValues;
      const { extraId, isCheckboxLabel, isCheckboxValue } =
        transformCheckboxExtraKey(linkedData?.extraId);
      const displayValue = mapValues?.[`${extraId}`]?.displayValue;
      if (isCheckboxLabel) {
        return (
          <BlackboardContentText
            value={displayValue || ""}
            style={style}
            zoomRatio={zoomRatio}
            boxProps={{ whiteSpace: "nowrap" }}
          />
        );
      }

      const newStyle = getComponentStyle(style);
      const isChecked = formData && formData == extraId;
      const size = `${newStyle.fontSize * zoomRatio}px`;
      const justifyContent = convertTextAlignToFlex(newStyle.textAlign);

      return (
        <Flex
          {...style}
          height="100%"
          width="100%"
          justifyContent={justifyContent}
        >
          <Checkbox
            isReadOnly={true}
            width="fit-content"
            pointerEvents="none"
            isChecked={isChecked}
            size="10px"
            css={{ "--checkbox-size": size }}
            sx={{
              svg: { width: size },
              ".chakra-checkbox__control": {
                borderRadius: "0px",
                borderWidth: "1px",
              },
              ".chakra-checkbox__control[data-checked] > div": {
                width: "100%",
              },
            }}
          >
            {isCheckboxValue ? "" : displayValue}
          </Checkbox>
        </Flex>
      );
    },
  },
  [DataType.DEVICE_PRESSURE]: {
    name: "機器連携：みるみるくん　圧力試験III",
    editor: DevicePressureEditor,
    raw: DevicePressureRaw,
    order: 7,
    dataType: DataType.DEVICE_PRESSURE,
    previewer: withReviewer(DevicePressurePreviewer),
    icon: <ConnectDeviceIcon w="2rem" h="2rem" />,
    getInitData: () => ({}),
    clearInitData: () => ({}),
    getContentPreview: (_, params) => {
      const { pinContent, component } = params;
      const field = component?.linkedData?.extraId;
      const data = pinContent?.value?.data?.[field || ""] || "";

      const updatedAt = pinContent?.updatedAt;
      const content = getContentNodeTypeNormalText({
        text: data,
        style: component?.style,
      });

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData, style, zoomRatio, linkedData } = params;
      const field = linkedData?.extraId;
      const data = formData?.[field || ""] || "";

      return (
        <BlackboardContentText
          value={data || ""}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
  },

  [DataType.PRE_FILLED]: {
    name: "記入済みテキスト",
    editor: PreFilledEditor,
    raw: PreFilledRaw,
    previewer: withReviewer(PreFilledPreviewer),
    icon: <TyphographyIcon w="2.5rem" h="2.5rem" />,
    order: 3,
    dataType: DataType.PRE_FILLED,
    getInitData: () => ({}),
    clearInitData: () => ({}),
    validate: () => {
      return z
        .object({
          value: z.string().optional(),
        })
        .nullish();
    },
    getContentPreview: (_, params) => {
      const { pinContent, component, itemResult } = params;
      const updatedAt = pinContent?.updatedAt;
      const text =
        pinContent?.value?.data ?? (itemResult?.additionInfo?.value || "");
      const content = getContentNodeTypeNormalText({
        text,
        style: component?.style,
      });

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData, style, itemResult, zoomRatio } = params;
      const additionInfo = itemResult?.additionInfo as PreFilledAdditionInfo;
      const defaultValue = additionInfo?.value || "";

      return (
        <BlackboardContentText
          value={formData ?? defaultValue}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
  },
  [DataType.DATE]: {
    name: "日付",
    editor: DateEditor,
    raw: DateRaw,
    previewer: withReviewer(DatePreviewer),
    order: 8,
    dataType: DataType.DATE,
    icon: <DateTimeIcon w="2.5rem" h="2.5rem" />,
    getInitData: (item) => {
      const additionInfo: DateAdditionalInfo = {
        ...item.additionInfo,
        formatType: DateFormatType.M_D,
        dateFromPhotoId: DEFAULT_DATE_FROM_PHOTO_ID,
      };

      return {
        additionInfo: structuredClone(additionInfo),
      } satisfies Partial<InspectionItemResult>;
    },
    clearInitData: () => {
      return {
        additionInfo: {},
      } satisfies Partial<InspectionItemResult>;
    },
    getContentPreview: (_, params) => {
      const { pinContent, itemResult, component } = params;
      const additionInfo = itemResult?.additionInfo as DateAdditionalInfo;
      const formatType = additionInfo?.formatType ?? DateFormatType.M_D;

      const updatedAt = pinContent?.updatedAt;
      const content = {
        type: NodeType.DATE,
        attrs: {
          date: pinContent?.value?.data,
          formatType,
          style: component?.style,
        },
      } satisfies CustomNode<DateNodeAttrs>;

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData, style, zoomRatio, itemResult } = params;
      const additionInfo = itemResult?.additionInfo as DateAdditionalInfo;
      const formatType = additionInfo?.formatType ?? DateFormatType.M_D;
      const formatString = DAYJS_FORMAT_STRING[formatType];
      const value = formatDate(formData, formatString);

      return (
        <BlackboardContentText
          value={value || ""}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
    validate: () => {
      const schema = z.object({
        formatType: z.number(),
      }) satisfies ZodType<DateAdditionalInfo | undefined>;

      return schema;
    },
  },
  [DataType.TIME]: {
    name: "時間",
    editor: TimeEditor,
    raw: TimeRaw,
    previewer: withReviewer(TimePreviewer),
    order: 8,
    dataType: DataType.TIME,
    icon: <TimeIcon w="2.5rem" h="2.5rem" />,
    getInitData: (item) => {
      const additionInfo: AdditionInfo = {
        ...item.additionInfo,
        formatType: TimeFormatType["HH:mm"],
      };

      return {
        additionInfo: structuredClone(additionInfo),
      } satisfies Partial<InspectionItemResult>;
    },
    clearInitData: () => {
      return {
        additionInfo: {},
      } satisfies Partial<InspectionItemResult>;
    },
    getContentPreview: (_, params) => {
      const { pinContent, component, itemResult } = params;
      const additionInfo = itemResult?.additionInfo as TimeAdditionalInfo;
      const formatType = additionInfo?.formatType ?? TimeFormatType["HH:mm"];

      const updatedAt = pinContent?.updatedAt;
      const content = {
        type: NodeType.TIME,
        attrs: {
          time: pinContent?.value?.data,
          formatType,
          style: component?.style,
        },
      } satisfies CustomNode<TimeNodeAttrs>;

      return { updatedAt, content };
    },
    getBlackboardContent: (params) => {
      const { formData, style, zoomRatio, itemResult } = params;
      const additionInfo = itemResult?.additionInfo as TimeAdditionalInfo;
      const formatType = additionInfo?.formatType ?? TimeFormatType["HH:mm"];
      const formatString = TIME_PICKER_FORMAT_STRING[formatType];
      const value = formatTimeByString(formData, formatString);

      return (
        <BlackboardContentText
          value={value || ""}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
    validate: () => {
      const schema = z.object({
        formatType: z.number(),
      }) satisfies ZodType<TimeAdditionalInfo | undefined>;

      return schema;
    },
  },
} satisfies Types;

export const typeOptions = Object.values(types)
  .sort((a, b) => a.order - b.order)
  .map((field) => {
    return {
      value: Number(field.dataType),
      title: field.name,
      label: (
        <Flex alignItems={"center"} gap="0.4rem">
          <Flex
            w="2.2rem"
            h="2.2rem"
            alignItems="center"
            justifyContent="center"
          >
            {field.icon}
          </Flex>
          <Text>{field.name}</Text>
        </Flex>
      ),
    };
  });

export const defaultType = DataType.TEXT;
